import { formatCurrency } from '@/shared/utils/currencyFormatter';
import {
  isCapitalGlobalBasicCoverage,
  usesBenefitSelection,
  usesBenefitSelectionWithPercent,
  type CapitalGlobalCoverageCatalogItem,
} from './coverages-catalog';

export type CoverageLimitsContext = {
  referenceCapital: number;
  morteCapital: number;
  morteId: string | undefined;
  /** Capitais atuais indexados pelo código InsureMO da cobertura. */
  coverageCapitalsByCode?: ReadonlyMap<string, number>;
};

export type CoverageEffectiveLimits = {
  min: number;
  max: number;
};

export function normalizeCoverageCode(code: string): string {
  return String(code).trim().replace(/^0+/, '');
}

/**
 * Capitais atuais indexados por código/id, para as coberturas
 * `PROPORTIONAL_TO_COVERAGE` resolverem sua base.
 *
 * Linha DESLIGADA não entra: `handleToggleCoverage` preserva o valor digitado ao
 * desligar, e o attach só envia linhas habilitadas — contar o capital de uma
 * cobertura ausente do payload faria a dependente ser precificada sobre algo que
 * não foi contratado. `enabled` ausente conta como ligada (linhas vindas de
 * chamadores que só carregam o capital).
 */
export function getCoverageCapitalsByCode(
  catalog: CapitalGlobalCoverageCatalogItem[],
  rows: Record<string, { enabled?: boolean; maxIndividualCapital: string }>
): Map<string, number> {
  const capitals = new Map<string, number>();

  for (const item of catalog) {
    const row = rows[item.id];
    if (row?.enabled === false) continue;

    const raw = row?.maxIndividualCapital ?? '';
    const capital = parseInt(String(raw).replace(/\D/g, ''), 10) / 100;
    if (Number.isFinite(capital) && capital > 0) {
      capitals.set(item.code, capital);
      capitals.set(normalizeCoverageCode(item.code), capital);
      capitals.set(item.id, capital);
      capitals.set(normalizeCoverageCode(item.id), capital);
      const productElementId = String(item.productElementId);
      capitals.set(productElementId, capital);
      capitals.set(normalizeCoverageCode(productElementId), capital);
    }
  }

  return capitals;
}

/**
 * Localiza a cobertura-base declarada em `referenceCoverageCode`.
 *
 * Aceita as mesmas chaves que `getCoverageCapitalsByCode` indexa (código, id e
 * `productElementId`, com zeros à esquerda tolerados) — se esta função acha a
 * cobertura, o capital dela é procurável pelo mesmo código.
 */
export function findCoverageByCode(
  catalog: CapitalGlobalCoverageCatalogItem[],
  code: string
): CapitalGlobalCoverageCatalogItem | undefined {
  const target = normalizeCoverageCode(code);
  if (!target) return undefined;

  return catalog.find(
    item =>
      normalizeCoverageCode(item.code) === target ||
      normalizeCoverageCode(item.id) === target ||
      normalizeCoverageCode(String(item.productElementId)) === target
  );
}

export function hasCatalogMax(item: CapitalGlobalCoverageCatalogItem): boolean {
  return item.limitMax != null && Number.isFinite(item.limitMax);
}

export function hasCatalogMin(item: CapitalGlobalCoverageCatalogItem): boolean {
  return item.limitMin != null && Number.isFinite(item.limitMin);
}

/**
 * Teto do campo `%` por cobertura. Morte permanece fixa em 100%;
 * demais usam `proportion` do catálogo ou 100% quando ausente.
 */
export function getMaxAllowedPercent(
  item: CapitalGlobalCoverageCatalogItem,
  morteId?: string
): number {
  if (morteId != null && item.id === morteId) {
    return 100;
  }
  return item.proportion != null && Number.isFinite(item.proportion)
    ? item.proportion
    : 100;
}

/**
 * Coberturas básicas usam o capital-base do risco; todas as demais usam a
 * cobertura declarada em `referenceCoverageCode` quando ela vier da API.
 */
export function getPercentReferenceCapital(
  item: CapitalGlobalCoverageCatalogItem,
  ctx: CoverageLimitsContext
): number {
  if (item.id === ctx.morteId) {
    return ctx.referenceCapital;
  }

  if (isCapitalGlobalBasicCoverage(item)) {
    return ctx.morteCapital;
  }

  if (item.referenceCoverageCode) {
    const referenceCapital =
      ctx.coverageCapitalsByCode?.get(item.referenceCoverageCode) ??
      ctx.coverageCapitalsByCode?.get(
        normalizeCoverageCode(item.referenceCoverageCode)
      );
    if (
      referenceCapital != null &&
      Number.isFinite(referenceCapital) &&
      referenceCapital > 0
    ) {
      return referenceCapital;
    }

    return 0;
  }

  return ctx.morteCapital;
}

/** Teto em R$ derivado do `proportion` (ou 100%) sobre o capital de referência da linha. */
export function getMaxCapitalFromPercent(
  item: CapitalGlobalCoverageCatalogItem,
  ctx: CoverageLimitsContext
): number {
  const maxPercent = getMaxAllowedPercent(item, ctx.morteId);
  const percentRef = getPercentReferenceCapital(item, ctx);
  if (percentRef <= 0 || maxPercent <= 0) return 0;
  return (percentRef * maxPercent) / 100;
}

/**
 * Minimum allowed capital (R$) from InsureMO when present; otherwise 0 (no product floor).
 */
export function getEffectiveMin(
  item: CapitalGlobalCoverageCatalogItem
): number {
  if (!hasCatalogMin(item)) {
    return 0;
  }
  return item.limitMin as number;
}

/**
 * Maximum allowed capital (R$) for a coverage row.
 * Morte → capital de referência do step 2; demais → min(limitMax, Morte × proportion%).
 */
export function getEffectiveMax(
  item: CapitalGlobalCoverageCatalogItem,
  ctx: CoverageLimitsContext
): number {
  const isMorte = item.id === ctx.morteId;

  if (isMorte) {
    if (!hasCatalogMax(item)) {
      return ctx.referenceCapital > 0 ? ctx.referenceCapital : 0;
    }
    return Math.min(ctx.referenceCapital, item.limitMax as number);
  }

  const maxFromPercent = getMaxCapitalFromPercent(item, ctx);
  if (maxFromPercent <= 0) return 0;

  if (!hasCatalogMax(item)) {
    return maxFromPercent;
  }

  return Math.min(item.limitMax as number, maxFromPercent);
}

export function getEffectiveLimits(
  item: CapitalGlobalCoverageCatalogItem,
  ctx: CoverageLimitsContext
): CoverageEffectiveLimits {
  return {
    min: getEffectiveMin(item),
    max: getEffectiveMax(item, ctx),
  };
}

/** Product min exceeds the capital ceiling allowed for this row (Morte / reference). */
export function isEffectiveRangeInvalid(
  limits: CoverageEffectiveLimits
): boolean {
  return limits.min > 0 && limits.max > 0 && limits.min > limits.max;
}

export function roundCoveragePercent(value: number): string {
  return String(Math.round(value * 100) / 100);
}

function parsePercentDraft(raw: string): number {
  const normalized = String(raw).trim().replace(',', '.');
  if (!normalized) return NaN;
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

/**
 * Pré-visualiza o capital enquanto o usuário digita o %.
 * Aplica apenas o teto em R$ — sem clamp de mínimo para não bloquear a edição.
 */
export function previewCapitalFromPercentDraft(
  percentDraft: string,
  item: CapitalGlobalCoverageCatalogItem,
  ctx: CoverageLimitsContext,
  formatCurrencyFn: (amount: number) => string
): string {
  const pct = parsePercentDraft(percentDraft);
  if (!Number.isFinite(pct) || pct <= 0) return '';

  const percentRef = getPercentReferenceCapital(item, ctx);
  if (percentRef <= 0) return '';

  const limits = getEffectiveLimits(item, ctx);
  let capital = (percentRef * pct) / 100;
  if (limits.max > 0 && capital > limits.max) {
    capital = limits.max;
  }

  return capital > 0 ? formatCurrencyFn(capital) : '';
}

/**
 * Confirma o % digitado: aplica teto percentual e limites min/max em R$.
 * Usado no blur do campo e antes da validação de submit.
 *
 * `currentCapital`: capital em R$ já armazenado na linha. O % fica guardado
 * arredondado a 2 casas (`roundCoveragePercent`), então derivar o capital dele
 * corrompe valores cuja proporção é dízima (R$ 5.000 sobre 84.000 → 5,95% →
 * R$ 4.998). Se o rascunho de % ainda é o arredondamento do capital atual, o
 * usuário não mudou o % — o capital digitado é mantido INTACTO, sem clamps:
 * valor fora dos limites min/max é acusado pela validação (mensagem de faixa
 * na linha), nunca corrigido por baixo dos panos.
 */
export function commitPercentAndCapital(
  percentDraft: string,
  item: CapitalGlobalCoverageCatalogItem,
  ctx: CoverageLimitsContext,
  formatCurrencyFn: (amount: number) => string,
  currentCapital?: number
): { maxIndividualCapital: string; percent: string } {
  const pct = parsePercentDraft(percentDraft);
  const percentRef = getPercentReferenceCapital(item, ctx);
  const limits = getEffectiveLimits(item, ctx);

  const draftMatchesCurrentCapital =
    currentCapital != null &&
    Number.isFinite(currentCapital) &&
    currentCapital > 0 &&
    percentRef > 0 &&
    Number.isFinite(pct) &&
    roundCoveragePercent((currentCapital / percentRef) * 100) ===
      roundCoveragePercent(pct);

  if (draftMatchesCurrentCapital) {
    return {
      maxIndividualCapital: formatCurrencyFn(currentCapital),
      percent: roundCoveragePercent(pct),
    };
  }

  if (!Number.isFinite(pct) || pct <= 0 || percentRef <= 0) {
    return { maxIndividualCapital: '', percent: '' };
  }

  const maxPct = getMaxAllowedPercent(item, ctx.morteId);
  const boundedPct = Math.min(pct, maxPct);

  const rawCapital = (percentRef * boundedPct) / 100;
  const { capital, percent } = capCapitalAndPercent({
    capital: rawCapital,
    percentRefCapital: percentRef,
    effectiveMax: limits.max,
    effectiveMin: limits.min,
  });

  return {
    maxIndividualCapital: capital > 0 ? formatCurrencyFn(capital) : '',
    percent,
  };
}

/**
 * Clamps capital to [effectiveMin, effectiveMax] and recalculates % against percentRefCapital.
 * Used when the user confirms % (blur/submit) or edits capital in R$.
 */
export function capCapitalAndPercent(params: {
  capital: number;
  percentRefCapital: number;
  effectiveMax: number;
  effectiveMin?: number;
}): { capital: number; percent: string } {
  const { percentRefCapital, effectiveMax } = params;
  const effectiveMin = params.effectiveMin ?? 0;
  let capital = params.capital;

  if (effectiveMax > 0 && capital > effectiveMax) {
    capital = effectiveMax;
  }
  if (effectiveMin > 0 && capital < effectiveMin) {
    capital = effectiveMin;
  }

  const percent =
    capital > 0 && percentRefCapital > 0
      ? roundCoveragePercent((capital / percentRefCapital) * 100)
      : '';

  return { capital, percent };
}

/**
 * Valores iniciais ao habilitar uma cobertura.
 * Default do `%` = min(100, teto InsureMO/`proportion`); o teto do input
 * continua em `getMaxAllowedPercent` (pode ser 80, 100, 200…).
 */
export function getInitialCoverageValues(
  item: CapitalGlobalCoverageCatalogItem,
  ctx: CoverageLimitsContext,
  formatCurrency: (amount: number) => string
): { maxIndividualCapital: string; percent: string } {
  // Capital escolhido entre opções de benefício: nada de pré-preencher — a
  // escolha do nível é do corretor (o seletor começa vazio). Exceção: nas
  // diárias (`BENEFIT_SELECTION_WITH_SI`) o % é input do valor da diária, então
  // pré-preenche como nas demais linhas (o seletor de nº de diárias segue vazio).
  if (usesBenefitSelection(item) && !usesBenefitSelectionWithPercent(item)) {
    return { maxIndividualCapital: '', percent: '' };
  }

  const limits = getEffectiveLimits(item, ctx);
  if (limits.max <= 0 || isEffectiveRangeInvalid(limits)) {
    return { maxIndividualCapital: '', percent: '' };
  }

  const percentRef = getPercentReferenceCapital(item, ctx);
  const maxPct = getMaxAllowedPercent(item, ctx.morteId);
  const defaultPct = Math.min(100, maxPct);
  const rawCapital = (percentRef * defaultPct) / 100;
  const { capital, percent } = capCapitalAndPercent({
    capital: rawCapital,
    percentRefCapital: percentRef,
    effectiveMax: limits.max,
    effectiveMin: limits.min,
  });

  return {
    maxIndividualCapital: capital > 0 ? formatCurrency(capital) : '',
    percent,
  };
}

export function getCapitalExceedsMaxMessage(
  item: CapitalGlobalCoverageCatalogItem,
  max: number
): string {
  return `Valor máximo para cobertura ${item.title} de ${formatCurrency(max)} foi excedido.`;
}

export function getCapitalBelowMinMessage(
  item: CapitalGlobalCoverageCatalogItem,
  min: number
): string {
  return `Valor mínimo para cobertura ${item.title} é ${formatCurrency(min)}.`;
}

/**
 * Faixa permitida da linha (limites EFETIVOS: o máximo já considera o
 * `proportion` sobre o capital de referência, não só o teto do catálogo).
 */
export function getCapitalOutOfRangeMessage(
  item: CapitalGlobalCoverageCatalogItem,
  limits: CoverageEffectiveLimits
): string {
  if (limits.min > 0 && limits.max > 0) {
    return `O valor da cobertura ${item.title} deve estar entre ${formatCurrency(limits.min)} e ${formatCurrency(limits.max)}.`;
  }
  if (limits.min > 0) {
    return getCapitalBelowMinMessage(item, limits.min);
  }
  return getCapitalExceedsMaxMessage(item, limits.max);
}

export function getInvalidEffectiveRangeMessage(
  item: CapitalGlobalCoverageCatalogItem
): string {
  return `Os limites da cobertura "${item.title}" são incompatíveis com o capital da Morte ou de referência.`;
}

/** Limits for Step 2 (RISK_BUSINESS) individual capital fields — direct Morte catalog bounds. */
export type MorteRiskStepLimits = {
  min: number | undefined;
  max: number | undefined;
  isRangeInvalid: boolean;
  source: 'catalog' | 'fallback';
  morteItem?: CapitalGlobalCoverageCatalogItem;
};

/**
 * Resolves the Morte catalog bounds consumed by Step 2 capital validation.
 *
 * Apenas o `min` chega a limitar o Capital Individual: o teto é a regra comercial
 * da banda — ver `resolveEffectiveIndividualCapitalRange`. O `max` continua aqui
 * porque é o que denuncia um catálogo incoerente (`isRangeInvalid`), e só nesse
 * caso o piso dele deixa de ser levado a sério.
 */
export function getMorteIndividualCapitalLimitsForRiskStep(
  morteItem: CapitalGlobalCoverageCatalogItem | undefined,
  fallbackMax: number | undefined
): MorteRiskStepLimits {
  if (!morteItem) {
    return {
      min: undefined,
      max: fallbackMax,
      isRangeInvalid: false,
      source: 'fallback',
    };
  }

  const min = hasCatalogMin(morteItem)
    ? (morteItem.limitMin as number)
    : undefined;
  const max = hasCatalogMax(morteItem)
    ? (morteItem.limitMax as number)
    : undefined;
  const isRangeInvalid = min !== undefined && max !== undefined && min > max;

  return {
    min,
    max,
    isRangeInvalid,
    source: 'catalog',
    morteItem,
  };
}
